import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Plus, Search, Home, Warehouse, Truck, Box as BoxIcon, Calendar, Download, AlertCircle, X, Clock, CheckCircle2, ChevronDown, Edit } from "lucide-react";
import { cn } from "@/lib/utils";
import endlessStorageLogo from "@/assets/endless-storage-logo.png";
import { useToast } from "@/hooks/use-toast";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { format, differenceInHours } from "date-fns";
import UserMenu from "@/components/UserMenu";
import { useProfileValidation } from "@/hooks/use-profile-validation";
import { EditPickupModal } from "@/components/EditPickupModal";
import { Carousel, CarouselContent, CarouselItem, CarouselNext, CarouselPrevious } from "@/components/ui/carousel";


// Map canonical time slot codes to human-friendly display
const timeSlotDisplay: Record<string, string> = {
  '9-11am': '9:00 AM - 11:00 AM',
  '12-2pm': '12:00 PM - 2:00 PM',
  '3-5pm': '3:00 PM - 5:00 PM',
  '6-8pm': '6:00 PM - 8:00 PM',
};
interface Box {
  id: string;
  box_number: string;
  name: string;
  location_status: string;
  room: string;
  thumbnail?: string;
  hasScheduledPickup?: boolean;
}
const DashboardConnected = () => {
  const navigate = useNavigate();
  const {
    toast
  } = useToast();
  const [loading, setLoading] = useState(true);
  const [boxes, setBoxes] = useState<Box[]>([]);
  const [upcomingPickups, setUpcomingPickups] = useState<any[]>([]);
  const [currentPickupIndex, setCurrentPickupIndex] = useState(0);
  const [searchQuery, setSearchQuery] = useState("");
  const [locationFilter, setLocationFilter] = useState<string>("all");
  const [showUpsellBanner, setShowUpsellBanner] = useState(true);
  const [user, setUser] = useState<any>(null);
  const [profile, setProfile] = useState<any>(null);
  const [dismissedPickupReminder, setDismissedPickupReminder] = useState(false);
  const [showActionMenu, setShowActionMenu] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [profileIncomplete, setProfileIncomplete] = useState(false);
  const [dismissedProfileBanner, setDismissedProfileBanner] = useState(false);
  const [showEditPickupModal, setShowEditPickupModal] = useState(false);
  
  const { validateProfile } = useProfileValidation();
  useEffect(() => {
    // Small delay to ensure auth state is fully settled after navigation
    const timer = setTimeout(() => {
      fetchData();
    }, 100);
    return () => clearTimeout(timer);
  }, []);
  const fetchData = async () => {
    try {
      const {
        data: {
          user: currentUser
        }
      } = await supabase.auth.getUser();
      if (!currentUser) {
        navigate('/auth');
        return;
      }
      setUser(currentUser);

      // Fetch profile
      const { data: profileData } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', currentUser.id)
        .single();
      
      setProfile(profileData);

      // Check dismissal status from localStorage
      const dismissalKey = `dismissedPickupReminder_${currentUser.id}`;
      const lastDismissed = localStorage.getItem(dismissalKey);
      if (lastDismissed) {
        const dismissedDate = new Date(lastDismissed);
        const daysSince = (Date.now() - dismissedDate.getTime()) / (1000 * 60 * 60 * 24);
        setDismissedPickupReminder(daysSince < 3);
      }

      // Check profile completeness
      const profileDismissalKey = `dismissedProfileBanner_${currentUser.id}`;
      const profileDismissed = localStorage.getItem(profileDismissalKey);
      if (profileDismissed) {
        const dismissedDate = new Date(profileDismissed);
        const daysSince = (Date.now() - dismissedDate.getTime()) / (1000 * 60 * 60 * 24);
        setDismissedProfileBanner(daysSince < 7);
      }

      const validation = await validateProfile();
      setProfileIncomplete(!validation.isComplete);

      // Fetch boxes
      const {
        data: boxesData,
        error: boxesError
      } = await supabase.from('boxes').select('*').eq('user_id', currentUser.id).order('created_at', {
        ascending: false
      });
      if (boxesError) throw boxesError;

      // Fetch upcoming shipments to mark boxes with scheduled pickups
      const { data: upcomingShipments } = await supabase
        .from('shipments')
        .select('box_ids')
        .eq('user_id', currentUser.id)
        .eq('type', 'to_storage')
        .eq('status', 'scheduled')
        .gte('pickup_date', new Date().toISOString());

      const scheduledBoxIds = new Set(
        upcomingShipments?.flatMap(s => s.box_ids) || []
      );

      // Fetch photos for each box
      const boxesWithPhotos = await Promise.all((boxesData || []).map(async box => {
        const {
          data: photos
        } = await supabase.from('photos').select('storage_path').eq('box_id', box.id).order('uploaded_at', {
          ascending: false
        }).limit(1);
        let thumbnail: string | undefined;
        if (photos && photos.length > 0) {
          const { data, error } = await supabase.storage
            .from('box-photos')
            .createSignedUrl(photos[0].storage_path, 3600); // 1 hour expiration
          thumbnail = error ? undefined : data.signedUrl;
        }
        return {
          ...box,
          thumbnail,
          hasScheduledPickup: scheduledBoxIds.has(box.id)
        };
      }));
      setBoxes(boxesWithPhotos);

      // Fetch all upcoming pickups
      const {
        data: shipmentsData
      } = await supabase.from('shipments').select('id, pickup_date, pickup_time_slot, box_ids, type, status').eq('user_id', currentUser.id).eq('type', 'to_storage').eq('status', 'scheduled').gte('pickup_date', new Date().toISOString()).order('pickup_date', {
        ascending: true
      });
      
      if (shipmentsData && shipmentsData.length > 0) {
        const pickupsWithBoxes = await Promise.all(
          shipmentsData.map(async (shipment) => {
            const { data: shipmentBoxes } = await supabase
              .from('boxes')
              .select('id, box_number, name')
              .in('id', shipment.box_ids);
            return {
              ...shipment,
              boxes: shipmentBoxes || []
            };
          })
        );
        setUpcomingPickups(pickupsWithBoxes);
      }
    } catch (error) {
      console.error('Error fetching data:', error);
      toast({
        title: "Error",
        description: "Failed to load data",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const handleDismissPickupReminder = () => {
    if (user) {
      const dismissalKey = `dismissedPickupReminder_${user.id}`;
      localStorage.setItem(dismissalKey, new Date().toISOString());
      setDismissedPickupReminder(true);
    }
  };

  const handleDismissProfileBanner = () => {
    if (user) {
      const dismissalKey = `dismissedProfileBanner_${user.id}`;
      localStorage.setItem(dismissalKey, new Date().toISOString());
      setDismissedProfileBanner(true);
    }
  };
  const filteredBoxes = boxes.filter(box => {
    const matchesSearch = box.name.toLowerCase().includes(searchQuery.toLowerCase()) || box.box_number.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesLocation = 
      locationFilter === "all" || 
      box.location_status === locationFilter ||
      (locationFilter === "pickup_scheduled" && box.hasScheduledPickup);
    return matchesSearch && matchesLocation;
  });
  const stats = {
    total: boxes.length,
    home: boxes.filter(b => b.location_status === "home").length,
    stored: boxes.filter(b => b.location_status === "stored").length,
    pickupScheduled: boxes.filter(b => b.hasScheduledPickup).length,
    monthlyCost: boxes.filter(b => b.location_status === "stored").length * 9.99
  };
  const getLocationBadge = (status: string) => {
    const variants: Record<string, {
      label: string;
      variant: any;
      icon: any;
    }> = {
      home: {
        label: "At Home",
        variant: "secondary",
        icon: Home
      },
      pickup_scheduled: {
        label: "Pickup Scheduled",
        variant: "outline",
        icon: Truck
      },
      in_transit: {
        label: "In Transit",
        variant: "outline",
        icon: Truck
      },
      stored: {
        label: "Stored",
        variant: "default",
        icon: Warehouse
      },
      returning: {
        label: "Returning",
        variant: "outline",
        icon: Truck
      }
    };
    const config = variants[status] || variants.home;
    const Icon = config.icon;
    return <Badge variant={config.variant} className="gap-1">
        <Icon className="h-3 w-3" />
        {config.label}
      </Badge>;
  };
  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-subtle flex flex-col items-center justify-center p-4">
        <img src={endlessStorageLogo} alt="Endless Storage" className="h-16 mb-6 opacity-80" />
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mb-4"></div>
        <p className="text-muted-foreground text-sm">Loading your inventory...</p>
      </div>
    );
  }
  return (
    <div className="min-h-screen bg-gradient-subtle">
      {/* Header */}
      <header className="bg-card border-b border-border sticky top-0 z-10 shadow-sm">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <button 
              onClick={() => navigate('/')}
              className="hover:opacity-80 transition-opacity"
            >
              <img src={endlessStorageLogo} alt="Endless Storage" className="h-12" />
            </button>
            <UserMenu 
              userEmail={user?.email || ""} 
              userName={profile?.full_name}
            />
          </div>
        </div>
      </header>

      <div className="container mx-auto px-4 py-8">
        {/* Status: pending_setup - Never started onboarding */}
        {profile?.onboarding_type === 'direct_storage_purchase' && profile?.onboarding_status === 'pending_setup' && (
          <Alert className="mb-6 border-2 border-orange-500/50 bg-orange-50 dark:bg-orange-950/20 animate-fade-in">
            <AlertCircle className="h-5 w-5 text-orange-600" />
            <AlertTitle className="text-orange-900 dark:text-orange-100">
              Complete Your Storage Setup
            </AlertTitle>
            <AlertDescription className="text-orange-800 dark:text-orange-200">
              <p className="mb-3">
                You purchased {profile.box_slots_purchased} box slots but haven't scheduled your FREE first pickup yet.
              </p>
              <Button 
                variant="outline"
                size="sm"
                className="border-orange-500 text-orange-700 hover:bg-orange-100 dark:text-orange-300"
                onClick={() => navigate('/onboarding/welcome')}
              >
                Complete Setup
              </Button>
            </AlertDescription>
          </Alert>
        )}

        {/* Status: skipped - Went to dashboard, dismissible reminder */}
        {profile?.onboarding_type === 'direct_storage_purchase' && 
         profile?.onboarding_status === 'skipped' && 
         upcomingPickups.length === 0 && 
         !dismissedPickupReminder && (
          <Alert className="mb-6 border-primary/50 bg-primary/5 animate-fade-in">
            <Calendar className="h-5 w-5 text-primary" />
            <AlertTitle>Ready to Schedule Pickup?</AlertTitle>
            <AlertDescription>
              <p className="mb-3">
                When you're ready, schedule your FREE first pickup for your {profile.box_slots_purchased} boxes.
              </p>
              <div className="flex gap-2">
                <Button 
                  variant="outline"
                  size="sm"
                  onClick={() => navigate('/onboarding/express-pickup?step=address')}
                >
                  Schedule Pickup
                </Button>
                <Button 
                  variant="ghost"
                  size="sm"
                  onClick={handleDismissPickupReminder}
                >
                  Remind Me Later
                </Button>
              </div>
            </AlertDescription>
          </Alert>
        )}

        {/* Status: address_confirmed - Started but didn't finish */}
        {profile?.onboarding_status === 'address_confirmed' && upcomingPickups.length === 0 && (
          <Alert className="mb-6 border-2 border-yellow-500/50 bg-yellow-50 dark:bg-yellow-950/20 animate-fade-in">
            <Clock className="h-5 w-5 text-yellow-600" />
            <AlertTitle className="text-yellow-900 dark:text-yellow-100">
              Almost There!
            </AlertTitle>
            <AlertDescription className="text-yellow-800 dark:text-yellow-200">
              <p className="mb-3">
                You confirmed your address. Finish scheduling your pickup to complete setup.
              </p>
              <Button 
                variant="outline"
                size="sm"
                className="border-yellow-500 text-yellow-700 hover:bg-yellow-100 dark:text-yellow-300"
                onClick={() => navigate('/onboarding/express-pickup?step=pickup')}
              >
                Schedule Pickup Now
              </Button>
            </AlertDescription>
          </Alert>
        )}

        {/* Profile Incomplete Banner */}
        {profileIncomplete && stats.home > 0 && !dismissedProfileBanner && (
          <Alert className="mb-6 border-orange-500/50 bg-orange-50 dark:bg-orange-950/20">
            <AlertCircle className="h-5 w-5 text-orange-600" />
            <AlertTitle className="text-orange-900 dark:text-orange-100">Complete Your Profile</AlertTitle>
            <AlertDescription className="text-orange-800 dark:text-orange-200">
              <p className="mb-3">
                You'll need to add your shipping address before scheduling a pickup.
              </p>
              <div className="flex gap-2">
                <Button 
                  variant="outline"
                  size="sm"
                  className="border-orange-500 text-orange-700 hover:bg-orange-100 dark:text-orange-300"
                  onClick={() => navigate('/settings')}
                >
                  Complete Profile
                </Button>
                <Button 
                  variant="ghost"
                  size="sm"
                  onClick={handleDismissProfileBanner}
                >
                  Dismiss
                </Button>
              </div>
            </AlertDescription>
          </Alert>
        )}

        {/* Upcoming Pickups Carousel */}
        {upcomingPickups.length > 0 && (
          <div className="mb-6 relative">
            <Carousel 
              className="w-full"
              opts={{ loop: false }}
            >
              <CarouselContent>
                {upcomingPickups.map((pickup, index) => (
                  <CarouselItem key={pickup.id}>
                    <Card className="border-2 border-primary/20 bg-primary/5 shadow-elegant animate-fade-in">
                      <CardContent className="pt-6">
                        <div className="flex items-start gap-4">
                          <div className="flex items-center justify-center w-12 h-12 rounded-full bg-primary/10">
                            <Calendar className="h-6 w-6 text-primary" />
                          </div>
                          <div className="flex-1">
                            <div className="flex items-start justify-between mb-1">
                              <h3 className="text-lg font-semibold">
                                Pickup scheduled for {format(new Date(pickup.pickup_date), "EEEE, MMMM d")}
                              </h3>
                              {upcomingPickups.length > 1 && (
                                <Badge variant="secondary" className="ml-2">
                                  {index + 1} of {upcomingPickups.length}
                                </Badge>
                              )}
                            </div>
                            <p className="text-muted-foreground mb-3">
                              {timeSlotDisplay[pickup.pickup_time_slot] || pickup.pickup_time_slot} • {pickup.boxes.length} boxes ready to ship
                            </p>
                            <div className="flex flex-wrap gap-2">
                              <Button 
                                variant="secondary" 
                                size="sm" 
                                onClick={() => navigate(`/checkout/success?session_id=view`)}
                              >
                                <CheckCircle2 className="h-4 w-4 mr-2" />
                                Review Checklist
                              </Button>
                              {differenceInHours(new Date(pickup.pickup_date), new Date()) >= 24 ? (
                                <Button 
                                  variant="outline" 
                                  size="sm" 
                                  onClick={() => {
                                    setCurrentPickupIndex(index);
                                    setShowEditPickupModal(true);
                                  }}
                                >
                                  <Edit className="h-4 w-4 mr-2" />
                                  Edit Pickup
                                </Button>
                              ) : (
                                <p className="text-xs text-muted-foreground mt-2">
                                  Pickup locked - less than 24 hours until scheduled time
                                </p>
                              )}
                            </div>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  </CarouselItem>
                ))}
              </CarouselContent>
              {upcomingPickups.length > 1 && (
                <>
                  <CarouselPrevious className="left-2" />
                  <CarouselNext className="right-2" />
                </>
              )}
            </Carousel>
          </div>
        )}

        {/* Upsell Banner */}
        {showUpsellBanner && stats.home > 0 && upcomingPickups.length === 0 && <div className="bg-gradient-accent text-white rounded-lg p-4 mb-6 shadow-sm relative">
            <Button variant="ghost" size="sm" className="absolute top-1 right-1 text-white hover:bg-white/20 h-6 w-6 p-0" onClick={() => setShowUpsellBanner(false)}>
              <X className="h-3 w-3" />
            </Button>
            <div className="flex items-center gap-3">
              <AlertCircle className="h-8 w-8 flex-shrink-0" />
              <div className="flex-1 min-w-0">
                <h3 className="font-bold mb-0.5">Ready to free up space?</h3>
                <p className="text-sm opacity-90">
                  Secure storage · $9.99/month per box
                </p>
              </div>
              <Button 
                variant="secondary"
                size="sm"
                className="ml-auto flex-shrink-0 bg-white text-primary hover:bg-white/90 font-semibold"
                onClick={() => navigate('/schedule-pickup')}
              >
                Get Started
              </Button>
            </div>
          </div>}

        {/* Stats Overview */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <Card className="shadow-sm">
            <CardContent className="p-6">
              <div className="text-3xl font-bold text-primary mb-1">{stats.total}</div>
              <div className="text-sm text-muted-foreground">Total Boxes</div>
            </CardContent>
          </Card>
          <Card className="shadow-sm">
            <CardContent className="p-6">
              <div className="text-3xl font-bold text-primary mb-1">{stats.home}</div>
              <div className="text-sm text-muted-foreground">At Home</div>
            </CardContent>
          </Card>
          <Card className="shadow-sm">
            <CardContent className="p-6">
              <div className="text-3xl font-bold text-primary mb-1">{stats.stored}</div>
              <div className="text-sm text-muted-foreground">In Storage</div>
            </CardContent>
          </Card>
          <Card className="shadow-sm">
            <CardContent className="p-6">
              <div className="text-3xl font-bold text-primary mb-1">{stats.pickupScheduled}</div>
              <div className="text-sm text-muted-foreground">Pickup Scheduled</div>
            </CardContent>
          </Card>
        </div>

        {/* Search and Filters */}
        <div className="flex flex-col gap-4 mb-6">
          {/* Search */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-muted-foreground" />
            <Input placeholder="Search boxes..." className="pl-10" value={searchQuery} onChange={e => setSearchQuery(e.target.value)} />
          </div>

          {/* Mobile: Collapsible filter */}
          <div className="md:hidden">
            <Button
              variant="outline"
              onClick={() => setShowFilters(!showFilters)}
              className="w-full justify-between"
            >
              <span className="flex items-center gap-2">
                Filter: {locationFilter === "all" ? "All" : locationFilter === "home" ? "Home" : locationFilter === "stored" ? "Storage" : "Pickup Scheduled"}
                {locationFilter !== "all" && (
                  <Badge variant="secondary" className="ml-1">1</Badge>
                )}
              </span>
              <ChevronDown className={cn("h-4 w-4 transition-transform", showFilters && "rotate-180")} />
            </Button>
            
            {showFilters && (
              <div className="grid grid-cols-2 gap-2 mt-3">
                <Button 
                  variant={locationFilter === "all" ? "default" : "outline"} 
                  onClick={() => { setLocationFilter("all"); setShowFilters(false); }}
                  size="sm"
                >
                  All
                </Button>
                <Button 
                  variant={locationFilter === "home" ? "default" : "outline"} 
                  onClick={() => { setLocationFilter("home"); setShowFilters(false); }}
                  size="sm"
                >
                  <Home className="h-4 w-4 mr-2" />
                  Home
                </Button>
                <Button 
                  variant={locationFilter === "stored" ? "default" : "outline"} 
                  onClick={() => { setLocationFilter("stored"); setShowFilters(false); }}
                  size="sm"
                >
                  <Warehouse className="h-4 w-4 mr-2" />
                  Storage
                </Button>
                <Button 
                  variant={locationFilter === "pickup_scheduled" ? "default" : "outline"} 
                  onClick={() => { setLocationFilter("pickup_scheduled"); setShowFilters(false); }}
                  size="sm"
                >
                  <Calendar className="h-4 w-4 mr-2" />
                  Scheduled
                </Button>
              </div>
            )}
          </div>

          {/* Desktop: Original inline filters with actions */}
          <div className="hidden md:flex gap-2 flex-wrap">
            <Button variant={locationFilter === "all" ? "default" : "outline"} onClick={() => setLocationFilter("all")}>
              All
            </Button>
            <Button variant={locationFilter === "home" ? "default" : "outline"} onClick={() => setLocationFilter("home")}>
              <Home className="h-4 w-4 mr-2" />
              Home
            </Button>
            <Button variant={locationFilter === "stored" ? "default" : "outline"} onClick={() => setLocationFilter("stored")}>
              <Warehouse className="h-4 w-4 mr-2" />
              Storage
            </Button>
            <Button variant={locationFilter === "pickup_scheduled" ? "default" : "outline"} onClick={() => setLocationFilter("pickup_scheduled")}>
              <Calendar className="h-4 w-4 mr-2" />
              Pickup Scheduled
            </Button>
            <Button className="bg-gradient-primary" onClick={() => navigate('/box/new')}>
              <Plus className="h-4 w-4 mr-2" />
              New Box
            </Button>
            {stats.home > 0 && <Button variant="outline" onClick={() => navigate('/schedule-pickup')}>
                <Truck className="h-4 w-4 mr-2" />
                Schedule Pickup
              </Button>}
          </div>
        </div>

        {/* Boxes Grid */}
        {filteredBoxes.length === 0 ? <div className="text-center py-20">
            <BoxIcon className="h-16 w-16 text-muted-foreground mx-auto mb-4 opacity-50" />
            <h3 className="text-2xl font-semibold mb-2">
              {boxes.length === 0 ? "Start Organizing!" : "No boxes found"}
            </h3>
            <p className="text-muted-foreground mb-6">
              {boxes.length === 0 ? "Create your first box to get started" : "Try adjusting your search or filters"}
            </p>
            {boxes.length === 0 && <Button size="lg" className="bg-gradient-primary" onClick={() => navigate('/box/new')}>
                <Plus className="h-5 w-5 mr-2" />
                Create First Box
              </Button>}
          </div> : <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredBoxes.map(box => <Card key={box.id} className="shadow-sm hover:shadow-elegant transition-smooth cursor-pointer" onClick={() => navigate(`/box/${box.id}`)}>
                <CardContent className="p-6">
                  <div className="aspect-video mb-4 rounded-lg bg-muted overflow-hidden">
                    {box.thumbnail ? <img src={box.thumbnail} alt={box.name} className="w-full h-full object-cover" /> : <div className="w-full h-full flex items-center justify-center bg-gradient-subtle">
                        <BoxIcon className="h-12 w-12 text-muted-foreground" />
                      </div>}
                  </div>
                  <div className="flex items-start justify-between mb-2">
                    <div>
                      <h3 className="font-semibold text-lg mb-1">{box.name}</h3>
                      <p className="text-sm text-muted-foreground">{box.box_number}</p>
                    </div>
                    {getLocationBadge(box.location_status)}
                   </div>
                   {box.hasScheduledPickup && upcomingPickups.length > 0 && (
                     <div className="mb-2 text-xs text-primary flex items-center gap-1">
                       <Calendar className="h-3 w-3" />
                       Pickup: {format(new Date(upcomingPickups[0].pickup_date), "MMM d")} • {timeSlotDisplay[upcomingPickups[0].pickup_time_slot]?.split(' - ')[0]}
                     </div>
                   )}
                  <div className="text-sm text-muted-foreground pt-3 border-t border-border">
                    {box.room}
                  </div>
                </CardContent>
              </Card>)}
          </div>}
      </div>

      {/* Mobile: Floating Action Button */}
      <div className="md:hidden">
        {/* Backdrop when menu is open */}
        {showActionMenu && (
          <div 
            className="fixed inset-0 bg-black/20 z-40 animate-in fade-in"
            onClick={() => setShowActionMenu(false)}
          />
        )}
        
        {/* Action Menu */}
        {showActionMenu && (
          <div className="fixed bottom-24 right-6 z-50 flex flex-col gap-2 animate-in slide-in-from-bottom-2">
            <Button
              className="shadow-lg h-14 px-6"
              onClick={() => {
                setShowActionMenu(false);
                navigate('/box/new');
              }}
            >
              <Plus className="h-5 w-5 mr-2" />
              New Box
            </Button>
            {stats.home > 0 && (
              <Button
                variant="outline"
                className="shadow-lg h-14 px-6 bg-card"
                onClick={() => {
                  setShowActionMenu(false);
                  navigate('/schedule-pickup');
                }}
              >
                <Truck className="h-5 w-5 mr-2" />
                Schedule Pickup
              </Button>
            )}
          </div>
        )}
        
        {/* FAB Button */}
        <Button
          size="lg"
          className={cn(
            "md:hidden fixed bottom-6 right-6 rounded-full shadow-glow h-14 w-14 p-0 z-10",
            showActionMenu && "hidden"
          )}
          onClick={() => setShowActionMenu(!showActionMenu)}
        >
          <Plus className="h-6 w-6" />
        </Button>
      </div>

      {/* Edit Pickup Modal */}
      {upcomingPickups.length > 0 && upcomingPickups[currentPickupIndex] && (
        <EditPickupModal
          shipmentId={upcomingPickups[currentPickupIndex].id}
          currentBoxIds={upcomingPickups[currentPickupIndex].box_ids}
          currentPickupDate={new Date(upcomingPickups[currentPickupIndex].pickup_date)}
          currentTimeSlot={upcomingPickups[currentPickupIndex].pickup_time_slot}
          isOpen={showEditPickupModal}
          onClose={() => setShowEditPickupModal(false)}
          onSuccess={fetchData}
        />
      )}
    </div>
  );
};

export default DashboardConnected;